import { RankWithSecurities } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityRanking/securityRankingTypes.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { SecurityPill } from '@dealstack/frontend/shared/components/SecurityPill/SecurityPill.tsx';
import { getSecurityFeatureTypeColors } from '@dealstack/frontend/shared/components/SecurityRankCard/securityFeatureTypeColors.ts';
import { SecurityRankCardContainer } from '@dealstack/frontend/shared/components/SecurityRankCard/SecurityRankCard.styles.tsx';
import { SwitchSecurityRankDropdown } from '@dealstack/frontend/shared/components/SecurityRankCard/SwitchSecurityRankDropdown.tsx';
import { SortTag } from '@dealstack/frontend/shared/components/SortTag/SortTag.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	useSecurityFeatureTypeTranslation,
	useSecurityTypeTranslation,
} from '@dealstack/frontend/shared/translationControllers/useCommonTranslationsController.ts';
import { SecurityFeatureType, SecurityType } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { Flex, Text, useMantineTheme } from '@mantine/core';
import { useMemo } from 'react';
import { MantineMenuItemProps } from '@dealstack/frontend/shared/components/MantineMenu/MantineMenu.types';

interface Props {
	name: string;
	securityType: SecurityType;
	securityFeatureType: SecurityFeatureType;
	isMergedIncomeRight: boolean;
	hasRatchetSchedule?: boolean;
	warrantOverName?: string;
	ranks?: RankWithSecurities[];
	onRankClick?: (rank: RankWithSecurities) => void;
	rankedRankId?: string;
	dragged?: boolean;
	/** A liquidation preference forces the income right split, so merging back is disallowed. */
	canMerge?: boolean;
	lockedInRank?: boolean;
	onUnrankClick?: () => void;
	onSplitClick?: () => void;
	onMergeClick?: () => void;
	onFinancialTermsClick?: () => void;
	cursor?: string;
}

export const SecurityRankCard = ({
	name,
	securityType,
	securityFeatureType,
	isMergedIncomeRight,
	hasRatchetSchedule = false,
	warrantOverName,
	rankedRankId,
	dragged = false,
	canMerge = true,
	lockedInRank = false,
	onUnrankClick,
	onSplitClick,
	onMergeClick,
	onFinancialTermsClick,
	cursor = 'pointer',
	ranks,
	onRankClick,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const tSecurityTypeTranslation = useSecurityTypeTranslation();
	const tSecurityFeatureTypeTranslation = useSecurityFeatureTypeTranslation();
	const mantineTheme = useMantineTheme();

	const securityTypeTranslation = tSecurityTypeTranslation(securityType);
	const { borderColor, color } = getSecurityFeatureTypeColors(mantineTheme, securityFeatureType);
	const isWarrant = !!warrantOverName;
	const cardName = isWarrant
		? `${name} (${tCapManage('warrant.over')} ${warrantOverName})`
		: name;
	const isIncomeRightFeature =
		isMergedIncomeRight ||
		securityFeatureType === SecurityFeatureType.INTEREST ||
		securityFeatureType === SecurityFeatureType.PRINCIPAL;
	const showActions = !dragged && (isIncomeRightFeature || rankedRankId) && !isWarrant;
	let securityFeatureTypes: string[] = [];

	if (isMergedIncomeRight) {
		securityFeatureTypes = [
			tSecurityFeatureTypeTranslation(SecurityFeatureType.INTEREST),
			tSecurityFeatureTypeTranslation(SecurityFeatureType.PRINCIPAL),
		];
	} else {
		securityFeatureTypes = [tSecurityFeatureTypeTranslation(securityFeatureType)];
	}

	const mappedRanks = useMemo<MantineMenuItemProps[] | undefined>(() => {
		return ranks
			?.filter((rank) => {
				if (rank.id === rankedRankId) {
					return false;
				}

				if (securityFeatureType === SecurityFeatureType.TRACKING) {
					return rank.isTracking;
				} else if (securityFeatureType === SecurityFeatureType.RESIDUE) {
					return rank.isResidue;
				} else {
					return !rank.isTracking && !rank.isResidue;
				}
			})
			?.map((rank) => {
				return {
					key: rank.id,
					label: (
						<Flex justify="space-between" align="center" gap="0.5rem">
							{rank.displayName} <SortTag label={rank.displaySortOrder} />
						</Flex>
					),
					onClick: () => onRankClick?.(rank),
				};
			});
	}, [ranks, rankedRankId, securityFeatureType, onRankClick]);

	return (
		<SecurityRankCardContainer
			data-testid={TEST_ELEMENT_IDS.CAP_SECURITY_RANK_CARD}
			style={{
				...(!isWarrant
					? {
							borderLeftColor: borderColor,
							transform: dragged ? 'rotateZ(-3deg)' : 'rotateZ(0)',
							boxShadow: dragged ? '0 16px 32px 0 rgba(145, 150, 152, 0.20)' : 'none',
							cursor,
						}
					: {
							transform: dragged ? 'rotateZ(-3deg)' : 'rotateZ(0)',
							boxShadow: dragged ? '0 16px 32px 0 rgba(145, 150, 152, 0.20)' : 'none',
						}),
			}}
			className={isWarrant ? 'warrant' : ''}
		>
			{isWarrant ? (
				<Icon variant="link-chain-light" size="lg" />
			) : (
				<Icon variant="grid-dots" size="lg" />
			)}
			<div className="content">
				<Text fw={600} c="dark.8" mb="0.5rem">
					{cardName}
				</Text>

				<div className="pills">
					<SecurityPill label={securityTypeTranslation} />

					{securityFeatureTypes.map((featureTypeLabel, index) => (
						<SecurityPill
							key={index}
							label={featureTypeLabel}
							color={color}
							borderColor={borderColor}
						/>
					))}

					{securityFeatureType === SecurityFeatureType.RESIDUE && hasRatchetSchedule && (
						<div
							data-testid={TEST_ELEMENT_IDS.CAP_SECURITY_RANK_CARD_RATCHET_INDICATOR}
						>
							<SecurityPill
								label={tCapManage('securities.ranking.ratchetIndicator')}
								color={mantineTheme.colors.grape[8]}
								borderColor={mantineTheme.colors.grape[6]}
							/>
						</div>
					)}
				</div>
			</div>
			{(showActions || onFinancialTermsClick) && (
				<div className="rank-actions">
					{rankedRankId && !lockedInRank && (
						<div
							className="remove"
							role="button"
							tabIndex={0}
							onClick={onUnrankClick}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									onUnrankClick?.();
								}
							}}
							data-testid={TEST_ELEMENT_IDS.CAP_SECURITY_UNRANK_BUTTON}
						>
							<Icon variant="close-v3" size="lg" />
						</div>
					)}

					<div className="bottom">
						{isMergedIncomeRight && (
							<div
								className="split-merge"
								role="button"
								tabIndex={0}
								onClick={onSplitClick}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										onSplitClick?.();
									}
								}}
								data-testid={TEST_ELEMENT_IDS.CAP_SECURITY_SPLIT_BUTTON}
							>
								<Icon variant="split" size="lg" />
							</div>
						)}

						{(securityFeatureType === SecurityFeatureType.INTEREST ||
							securityFeatureType === SecurityFeatureType.PRINCIPAL) &&
							!isMergedIncomeRight &&
							canMerge && (
								<div
									className="split-merge"
									role="button"
									tabIndex={0}
									onClick={onMergeClick}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											onMergeClick?.();
										}
									}}
									data-testid={TEST_ELEMENT_IDS.CAP_SECURITY_MERGE_BUTTON}
								>
									<Icon variant="merge" size="lg" />
								</div>
							)}

						{onFinancialTermsClick && (
							<div
								className="financial-terms"
								role="button"
								tabIndex={0}
								onClick={onFinancialTermsClick}
								onKeyDown={(e) => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										onFinancialTermsClick();
									}
								}}
							>
								<Icon variant="certificate-awarded" size="lg" />
							</div>
						)}

						{!!mappedRanks?.length && rankedRankId && !lockedInRank && (
							<SwitchSecurityRankDropdown ranks={mappedRanks} />
						)}
					</div>
				</div>
			)}
		</SecurityRankCardContainer>
	);
};
